import { csiAPI } from "src/boot/axios"
import { CSIurl } from "src/util/enums/urls"
import {
  appendAttachmentToFormData,
  concatUrl,
  getCustomConfig,
  getNewAbortHandler,
  handleAttachmentBatchResponses,
} from "src/util/http/httpHelper"
import { Attachment, AttachmentUpload } from "src/util/types/dataModels/attachment"
import {
  ItemModelAttributeCharValue,
  ItemModelAttributeNumericValue,
  ItemModelAttributeSelectValue,
  ItemModelAttributeTemporalValue,
} from "src/util/types/dataModels/attribute"
import { BatchErrors, ObjectList, PayloadEntity } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import { ItemModel } from "src/util/types/dataModels/itemModel"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_DELETE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_ATTRIBUTES,
  PERMISSION_ITEMS,
} from "src/util/types/dataModels/user"

//region Item Models

/**
 * Get the list of all existing Item Models
 *
 * @param config custom configurations and filtering params
 */
export async function getItemModelsList(config: HttpConfig = {}): Promise<ObjectList<ItemModel>> {
  const entityURL = CSIurl.ITEM_MODELS
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create a new Item Model
 *
 * @param config custom configurations and filtering params
 * @param itemModel
 */
export async function postItemModel(
  config: HttpConfig = {},
  itemModel: PayloadEntity<ItemModel>
): Promise<ItemModel> {
  const url = CSIurl.ITEM_MODELS
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.post(url, itemModel, { signal, customConfig })

  return data
}

/**
 * Get an individual Item Model
 *
 * @param config custom configurations and filtering params
 * @param itemModelId
 */
export async function getItemModel(
  config: HttpConfig = {},
  itemModelId: string
): Promise<ItemModel> {
  const url = CSIurl.ITEM_MODELS_ID.replace("{id}", itemModelId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing individual Item Model
 *
 * @param config custom configurations and filtering params
 * @param itemModelId
 * @param itemModel
 */
export async function putItemModel(
  config: HttpConfig = {},
  itemModelId: string,
  itemModel: PayloadEntity<ItemModel>
): Promise<ItemModel> {
  const url = CSIurl.ITEM_MODELS_ID.replace("{id}", itemModelId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.put(url, itemModel, { signal, customConfig })

  return data
}

/**
 * (Partially) update an individual Item Model
 *
 * @param config custom configurations and filtering params
 * @param id
 * @param itemModel
 */
export async function patchItemModel(
  config: HttpConfig = {},
  id: string,
  itemModel: Partial<PayloadEntity<ItemModel>>
): Promise<ItemModel> {
  const url = CSIurl.ITEM_MODELS_ID.replace("{id}", id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_UPDATE)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, itemModel, { signal, customConfig })

  return data
}

//endregion

//region Item Models Char Values

export async function getItemModelsAttributeCharValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string
): Promise<ItemModelAttributeCharValue> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_CHAR_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function patchItemModelsAttributeCharValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string,
  payload: Partial<PayloadEntity<ItemModelAttributeCharValue>>
): Promise<ItemModelAttributeCharValue> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_CHAR_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

export async function deleteItemModelsAttributeCharValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string
) {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_CHAR_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getItemModelsAttributeCharValues(
  config: HttpConfig = {},
  itemModelId: string
): Promise<ItemModelAttributeCharValue[]> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_CHAR_VALUES.replace("{item_model_id}", itemModelId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region Item Models Numeric Values

export async function getItemModelsAttributeNumericValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string
): Promise<ItemModelAttributeNumericValue> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_NUMERIC_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function patchItemModelsAttributeNumericValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string,
  payload: Partial<PayloadEntity<ItemModelAttributeNumericValue>>
): Promise<ItemModelAttributeNumericValue> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_NUMERIC_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

export async function deleteItemModelsAttributeNumericValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string
) {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_NUMERIC_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getItemModelsAttributeNumericValues(
  config: HttpConfig = {},
  itemModelId: string
): Promise<ItemModelAttributeNumericValue[]> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_NUMERIC_VALUES.replace("{item_model_id}", itemModelId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region Item Models Select Values

export async function getItemModelsAttributeSelectValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string
): Promise<ItemModelAttributeSelectValue> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_SELECT_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function patchItemModelsAttributeSelectValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string,
  payload: Partial<PayloadEntity<ItemModelAttributeSelectValue>>
): Promise<ItemModelAttributeSelectValue> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_SELECT_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

// TODO-NOTE: Currently throws 500, cloud team is aware
export async function deleteItemModelsAttributeSelectValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string
) {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_SELECT_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getItemModelsAttributeSelectValues(
  config: HttpConfig = {},
  itemModelId: string
): Promise<ItemModelAttributeSelectValue[]> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_SELECT_VALUES.replace("{item_model_id}", itemModelId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region Item Models Temporal Values

export async function getItemModelsAttributeTemporalValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string
): Promise<ItemModelAttributeTemporalValue> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_TEMPORAL_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function patchItemModelsAttributeTemporalValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string,
  payload: Partial<PayloadEntity<ItemModelAttributeTemporalValue>>
): Promise<ItemModelAttributeTemporalValue> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_TEMPORAL_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.patch(url, payload, { signal, customConfig })

  return data
}

export async function deleteItemModelsAttributeTemporalValue(
  config: HttpConfig = {},
  itemModelId: string,
  attributeId: string
) {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_TEMPORAL_VALUE.replace(
    "{item_model_id}",
    itemModelId
  ).replace("{attr_id}", attributeId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("DELETE", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_ATTRIBUTES,
    PERMISSION_ACCESS_DELETE
  )
  const signal = abortHandler.controller.signal

  await csiAPI.delete(url, { signal, customConfig })
}

export async function getItemModelsAttributeTemporalValues(
  config: HttpConfig = {},
  itemModelId: string
): Promise<ItemModelAttributeTemporalValue[]> {
  const url = CSIurl.ITEM_MODELS_ATTRIBUTES_TEMPORAL_VALUES.replace("{item_model_id}", itemModelId)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ATTRIBUTES, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region All Values for Item Models

export async function getItemModelsAttributeCharValuesList(
  config: HttpConfig = {}
): Promise<ObjectList<ItemModelAttributeCharValue>> {
  const entityURL = CSIurl.ITEM_MODELS_ATTRIBUTES_CHAR_VALUES_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function getItemModelsAttributeNumericValuesList(
  config: HttpConfig = {}
): Promise<ObjectList<ItemModelAttributeNumericValue>> {
  const entityURL = CSIurl.ITEM_MODELS_ATTRIBUTES_NUMERIC_VALUES_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function getItemModelsAttributeSelectValuesList(
  config: HttpConfig = {}
): Promise<ObjectList<ItemModelAttributeSelectValue>> {
  const entityURL = CSIurl.ITEM_MODELS_ATTRIBUTES_SELECT_VALUES_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

export async function getItemModelsAttributeTemporalValuesList(
  config: HttpConfig = {}
): Promise<ObjectList<ItemModelAttributeTemporalValue>> {
  const entityURL = CSIurl.ITEM_MODELS_ATTRIBUTES_TEMPORAL_VALUES_LIST
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { signal, customConfig })

  return data
}

//endregion

//region Attachments

export async function getItemModelAttachmentList(
  config: HttpConfig = {},
  model_id: string
): Promise<ObjectList<Attachment>> {
  const entityURL = CSIurl.ITEM_MODELS_ID_ATTACHMENTS_LIST.replace("{model_id}", model_id)
  const url = concatUrl(entityURL, { ...config?.mainParams })
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: ObjectList<Attachment> } = await csiAPI.get(url, { signal, customConfig })

  data.results = data.results.map((i) => {
    i.context = ["item_model"]
    return i
  })

  return data
}

export async function postItemModelAttachment(
  config: HttpConfig = {},
  model_id: string,
  attachment: AttachmentUpload
): Promise<Attachment> {
  const url = CSIurl.ITEM_MODELS_ID_ATTACHMENTS_LIST.replace("{model_id}", model_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const formData = appendAttachmentToFormData(attachment)

  const { data }: { data: Attachment } = await csiAPI.post(url, formData, {
    headers,
    signal,
    customConfig,
  })

  data.context = ["item_model"]

  return data
}

export async function getItemModelAttachment(
  config: HttpConfig = {},
  model_id: string,
  attachmentId: string
): Promise<Attachment> {
  const url = CSIurl.ITEM_MODELS_ID_ATTACHMENTS_ID.replace("{model_id}", model_id).replace(
    "{id}",
    attachmentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data }: { data: Attachment } = await csiAPI.get(url, { signal, customConfig })

  data.context = ["item_model"]

  return data
}

export async function getItemModelAttachmentContent(
  config: HttpConfig = {},
  model_id: string,
  attachmentId: string
): Promise<Blob> {
  const url = CSIurl.ITEM_MODELS_ID_ATTACHMENTS_ID_CONTENT.replace("{model_id}", model_id).replace(
    "{id}",
    attachmentId
  )
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const headers = {
    "Content-Type": "multipart/form-data",
  }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_READ)
  const signal = abortHandler.controller.signal

  const { data } = await csiAPI.get(url, { headers, signal, responseType: "blob", customConfig })

  return data
}

export async function postItemModelAttachmentBatch(
  config: HttpConfig = {},
  model_id: string,
  attachments: AttachmentUpload[]
): Promise<ObjectList<Attachment> | ObjectList<BatchErrors | Attachment>> {
  const url = CSIurl.ITEM_MODELS_ID_ATTACHMENTS_BATCH.replace("{model_id}", model_id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const headers = { "Content-Type": "multipart/form-data" }
  const customConfig = getCustomConfig(abortHandler, PERMISSION_ITEMS, PERMISSION_ACCESS_CREATE)
  const signal = abortHandler.controller.signal

  const formData = new FormData()

  for (const attachment of attachments) {
    appendAttachmentToFormData(attachment, formData)
  }

  const response = await csiAPI.post(url, formData, { headers, signal, customConfig })

  return handleAttachmentBatchResponses(response, "item_model")
}

//endregion
